import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../context/CurrencyContext';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  Video, 
  Coffee,
  Sparkles,
  ArrowLeft,
  Check,
  Share2,
  AlertCircle,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { apiGet, apiPost } from '../lib/api';
import notify from '../lib/notify';
import SEO from '../components/SEO';

const EVENT_TYPE_CONFIG = {
  meetup: { label: 'Meetup', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', icon: Users },
  workshop: { label: 'Workshop', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', icon: Sparkles },
  webinar: { label: 'Webinar', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: Video },
  networking: { label: 'Networking', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', icon: Users },
  fika: { label: 'Fika', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', icon: Coffee },
};

export default function EventDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formatPrice, convert, currency: userCurrency } = useCurrency();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registering, setRegistering] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Convert and format currency using user's preferred currency
  const formatAmount = (amount, sourceCurrency = 'SEK') => {
    const converted = convert(amount, sourceCurrency, userCurrency);
    return formatPrice(converted, userCurrency);
  };

  // Check for success/cancelled from Stripe redirect
  useEffect(() => {
    const registration = searchParams.get('registration');
    if (registration === 'success') {
      notify.success('Successfully registered for the event!');
    } else if (registration === 'cancelled') {
      notify.info('Registration cancelled');
    }
  }, [searchParams]);

  // Check auth status
  useEffect(() => {
    try {
      const token = localStorage.getItem('auth:token');
      setIsLoggedIn(Boolean(token));
    } catch (e) {
      setIsLoggedIn(false);
    }
  }, []);

  // Fetch event details
  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiGet(`/events/${id}`);
        const eventData = res?.data?.event || res?.event;
        if (!eventData) {
          setError('Event not found');
        } else {
          setEvent(eventData);
        }
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to load event');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchEvent();
  }, [id]);

  // Helper to get event dates (handles both field name formats)
  const getEventDate = () => event?.starts_at || event?.start_at;
  const getEventEndDate = () => event?.ends_at || event?.end_at;
  const getEventLocation = () => event?.location || event?.venue_name || event?.venue_address || 'Location TBA';
  const getEventCapacity = () => event?.capacity || event?.max_attendees;
  const isEventOnline = () => event?.is_online || 
    (event?.venue_name || '').toLowerCase().includes('online') ||
    (event?.location || '').toLowerCase().includes('online');

  const handleRegister = async () => {
    if (!isLoggedIn) {
      navigate(`/login?redirect=/events/${id}`);
      return;
    }

    setRegistering(true);
    try {
      const res = await apiPost('/event-registrations', { event_id: id });
      const data = res?.data || res;

      if (data.payment_required && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      notify.success(data.message || 'Successfully registered!');
      
      const eventRes = await apiGet(`/events/${id}`);
      setEvent(eventRes?.data?.event || eventRes?.event);
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.message || 'Registration failed';
      if (errMsg.includes('Already registered')) {
        notify.info('You are already registered for this event');
      } else {
        notify.error(errMsg);
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = event?.title || 'Event';
    
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (e) {
        // User cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        notify.success('Link copied to clipboard!');
      } catch (e) {
        notify.error('Failed to copy link');
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (startStr, endStr) => {
    if (!startStr) return '';
    const start = new Date(startStr);
    const startTime = start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    
    if (endStr) {
      const end = new Date(endStr);
      const endTime = end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      
      const durationMs = end - start;
      const hours = Math.floor(durationMs / (1000 * 60 * 60));
      const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
      let duration = '';
      if (hours > 0) duration += `${hours}h`;
      if (minutes > 0) duration += ` ${minutes}m`;
      
      return `${startTime} - ${endTime}${duration ? ` (${duration.trim()})` : ''}`;
    }
    return startTime;
  };

  const getEventTypeConfig = (type) => {
    return EVENT_TYPE_CONFIG[type] || { label: type || 'Event', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: Calendar };
  };

  const isUserRegistered = Boolean(event?.user_registration);
  const capacity = getEventCapacity();
  const isFull = capacity && event?.registration_count >= capacity;
  const eventDate = getEventDate();
  const isPast = eventDate && new Date(eventDate) < new Date();
  const isCancelled = event?.status === 'cancelled';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center py-16">
        <AlertCircle className="w-16 h-16 text-zinc-300 dark:text-zinc-600 mb-4" />
        <h1 className="text-2xl font-bold text-primary-text mb-2">
          Event Not Found
        </h1>
        <p className="text-secondary-text mb-6">
          {error || 'This event may have been removed or doesn\'t exist.'}
        </p>
        <Link to="/events" className="btn btn-primary">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Events
        </Link>
      </div>
    );
  }

  const typeConfig = getEventTypeConfig(event.event_type);
  const TypeIcon = typeConfig.icon;
  const spotsRemaining = capacity ? capacity - (event.registration_count || 0) : null;
  const eventLocation = getEventLocation();
  const isOnline = isEventOnline();
  const eventEndDate = getEventEndDate();

  return (
    <>
      <SEO 
        title={`${event.title} - Kreatives Events`}
        description={event.description?.slice(0, 160) || `Join us for ${event.title}`}
        image={event.image_url}
      />

      <div className="min-h-screen flex flex-col">
        <main className="flex-1">
          {/* Back Link */}
          <section className="py-4 px-4">
            <div className="container mx-auto max-w-6xl">
              <Link 
                to="/events" 
                className="inline-flex items-center gap-2 text-secondary-text hover:text-primary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Events
              </Link>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-6 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Hero Image */}
                  <div className="relative rounded-2xl overflow-hidden">
                    {event.image_url ? (
                      <img
                        src={event.image_url}
                        alt={event.title}
                        className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                      />
                    ) : (
                      <div className="w-full h-64 sm:h-80 lg:h-96 bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
                        <TypeIcon className="w-24 h-24 text-primary/50" />
                      </div>
                    )}

                    {/* Badges */}
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium ${typeConfig.color}`}>
                        <TypeIcon className="w-4 h-4" />
                        {typeConfig.label}
                      </span>
                      {isOnline && (
                        <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                          <Video className="w-4 h-4" />
                          Online
                        </span>
                      )}
                      {isCancelled && (
                        <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                          Cancelled
                        </span>
                      )}
                    </div>

                    {/* Free Badge */}
                    {event.is_free && (
                      <div className="absolute top-4 right-4">
                        <span className="px-4 py-2 rounded-full text-sm font-bold bg-emerald-500 text-white shadow-lg">
                          FREE
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-primary-text mb-4">
                      {event.title}
                    </h1>
                    
                    <div className="flex flex-wrap gap-4 text-secondary-text">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        <span>{formatDate(eventDate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        <span>{formatTime(eventDate, eventEndDate)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-card-bg rounded-2xl border border-card-border p-6 sm:p-8">
                    <h2 className="text-xl font-semibold text-primary-text mb-4">
                      About This Event
                    </h2>
                    <div className="prose prose-zinc dark:prose-invert max-w-none">
                      <p className="text-secondary-text whitespace-pre-wrap">
                        {event.description || 'No description available.'}
                      </p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="bg-card-bg rounded-2xl border border-card-border p-6 sm:p-8">
                    <h2 className="text-xl font-semibold text-primary-text mb-4">
                      Location
                    </h2>
                    <div className="flex items-start gap-3">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        {isOnline ? (
                          <Video className="w-6 h-6 text-primary" />
                        ) : (
                          <MapPin className="w-6 h-6 text-primary" />
                        )}
                      </div>
                      <div>
                        <p className="text-primary-text font-medium">
                          {eventLocation}
                        </p>
                        {event.venue_address && event.venue_name && event.venue_address !== event.venue_name && (
                          <p className="text-sm text-secondary-text mt-1">
                            {event.venue_address}
                          </p>
                        )}
                        {isOnline && (
                          <p className="text-sm text-secondary-text mt-1">
                            A link will be sent to registered attendees before the event.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="lg:col-span-1">
                  <div className="sticky top-24 space-y-6">
                    {/* Registration Card */}
                    <div className="bg-card-bg rounded-2xl border border-card-border p-6 shadow-lg">
                      {/* Status */}
                      {isUserRegistered ? (
                        <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl mb-6">
                          <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                          <div>
                            <p className="font-medium text-emerald-700 dark:text-emerald-300">
                              You're registered!
                            </p>
                            <p className="text-sm text-emerald-600 dark:text-emerald-400">
                              Status: {event.user_registration.status}
                            </p>
                          </div>
                        </div>
                      ) : isCancelled ? (
                        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl mb-6">
                          <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                          <p className="font-medium text-red-700 dark:text-red-300">
                            This event has been cancelled
                          </p>
                        </div>
                      ) : isPast ? (
                        <div className="flex items-center gap-3 p-4 bg-subtle-bg rounded-xl mb-6">
                          <Clock className="w-6 h-6 text-secondary-text" />
                          <p className="font-medium text-secondary-text">
                            This event has ended
                          </p>
                        </div>
                      ) : isFull ? (
                        <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl mb-6">
                          <Users className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                          <p className="font-medium text-amber-700 dark:text-amber-300">
                            This event is full
                          </p>
                        </div>
                      ) : null}

                      {/* Price */}
                      <div className="text-center mb-6">
                        <span className="text-3xl font-bold text-primary-text">
                          {event.is_free ? 'Free' : formatAmount(event.price, event.currency)}
                        </span>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="text-center p-3 bg-subtle-bg rounded-xl">
                          <div className="text-2xl font-bold text-primary-text">
                            {event.registration_count || 0}
                          </div>
                          <div className="text-sm text-secondary-text">
                            Registered
                          </div>
                        </div>
                        {capacity && (
                          <div className="text-center p-3 bg-subtle-bg rounded-xl">
                            <div className="text-2xl font-bold text-primary-text">
                              {spotsRemaining}
                            </div>
                            <div className="text-sm text-secondary-text">
                              Spots Left
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Capacity Bar */}
                      {capacity && (
                        <div className="mb-6">
                          <div className="flex justify-between text-sm text-secondary-text mb-2">
                            <span>{event.registration_count || 0} / {capacity}</span>
                            <span>{Math.round(((event.registration_count || 0) / capacity) * 100)}% full</span>
                          </div>
                          <div className="h-2 bg-subtle-bg rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: `${Math.min(100, ((event.registration_count || 0) / capacity) * 100)}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Register Button */}
                      {!isUserRegistered && !isCancelled && !isPast && !isFull && (
                        <button
                          onClick={handleRegister}
                          disabled={registering}
                          className="w-full py-3 px-6 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                          {registering ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Registering...
                            </>
                          ) : (
                            <>
                              <Check className="w-5 h-5" />
                              {isLoggedIn ? 'Register Now' : 'Login to Register'}
                            </>
                          )}
                        </button>
                      )}

                      {/* Share Button */}
                      <button
                        onClick={handleShare}
                        className="w-full mt-3 py-3 px-6 bg-subtle-bg text-primary-text rounded-xl font-medium hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Share2 className="w-5 h-5" />
                        Share Event
                      </button>
                    </div>

                    {/* Event Details */}
                    <div className="bg-card-bg rounded-2xl border border-card-border p-6">
                      <h3 className="font-semibold text-primary-text mb-4">
                        Event Details
                      </h3>
                      <div className="space-y-4 text-sm">
                        <div className="flex items-start gap-3">
                          <Calendar className="w-5 h-5 text-secondary-text mt-0.5" />
                          <div>
                            <p className="font-medium text-primary-text">Date</p>
                            <p className="text-secondary-text">{formatDate(eventDate)}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Clock className="w-5 h-5 text-secondary-text mt-0.5" />
                          <div>
                            <p className="font-medium text-primary-text">Time</p>
                            <p className="text-secondary-text">{formatTime(eventDate, eventEndDate)}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          {isOnline ? (
                            <Video className="w-5 h-5 text-secondary-text mt-0.5" />
                          ) : (
                            <MapPin className="w-5 h-5 text-secondary-text mt-0.5" />
                          )}
                          <div>
                            <p className="font-medium text-primary-text">Location</p>
                            <p className="text-secondary-text">{eventLocation}</p>
                          </div>
                        </div>
                        {capacity && (
                          <div className="flex items-start gap-3">
                            <Users className="w-5 h-5 text-secondary-text mt-0.5" />
                            <div>
                              <p className="font-medium text-primary-text">Capacity</p>
                              <p className="text-secondary-text">{capacity} attendees</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}
